import { ComponentPropsWithoutRef, ComponentRef, CSSProperties, FC, ReactNode, Ref } from 'react';
import classNames from 'classnames';
import { Dialog as RadixDialog } from 'radix-ui';

import ModalBody from './ModalBody/ModalBody';
import ModalClose from './ModalClose/ModalClose';
import ModalFooter from './ModalFooter/ModalFooter';
import ModalHeader from './ModalHeader/ModalHeader';
import styles from './Modal.module.css';

type ModalContentPassthroughProps = Omit<ComponentPropsWithoutRef<typeof RadixDialog.Content>, 'className' | 'style'>;

export type ModalProps = {
  /** Открыта ли модалка — управляется снаружи. */
  open: boolean;
  /** Вызывается при любой попытке открыть/закрыть (Escape, клик снаружи, `Modal.Close`). */
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  /** Ширина поверх авто-фита под контент (в пределах min/max). `"100%"` — замена FULL_HEIGHT_LARGE. */
  width?: number | string;
  /** Высота поверх авто-фита под контент (в пределах max-height). */
  height?: number | string;
  style?: CSSProperties;
  /** Класс для оверлея-подложки. */
  overlayClassName?: string;
  /** Класс для самого окна (контента). */
  contentClassName?: string;
  /** Можно ли закрыть модалку по Escape и клику снаружи. По умолчанию `true`. */
  dismissible?: boolean;
  ref?: Ref<ComponentRef<typeof RadixDialog.Content>>;
} & ModalContentPassthroughProps;

/** Модальное окно для десктопа на Radix Dialog. */
const Modal: FC<ModalProps> = ({
  open,
  onOpenChange,
  children,
  width,
  height,
  style,
  overlayClassName,
  contentClassName,
  dismissible = true,
  ref,
  'aria-describedby': ariaDescribedBy,
  onEscapeKeyDown,
  onPointerDownOutside,
  ...rest
}) => {
  const handleEscapeKeyDown: NonNullable<ModalContentPassthroughProps['onEscapeKeyDown']> = (event) => {
    onEscapeKeyDown?.(event);
    if (!dismissible) event.preventDefault();
  };

  const handlePointerDownOutside: NonNullable<ModalContentPassthroughProps['onPointerDownOutside']> = (event) => {
    onPointerDownOutside?.(event);
    if (!dismissible) event.preventDefault();
  };

  return (
    <RadixDialog.Root
      open={open}
      onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={classNames(styles.overlay, overlayClassName)} />
        <RadixDialog.Content
          ref={ref}
          aria-describedby={ariaDescribedBy}
          {...rest}
          onEscapeKeyDown={handleEscapeKeyDown}
          onPointerDownOutside={handlePointerDownOutside}
          className={classNames(styles.content, contentClassName)}
          style={{ ...style, width, height }}>
          {children}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
};

type ModalCompound = typeof Modal & {
  Header: typeof ModalHeader;
  Body: typeof ModalBody;
  Footer: typeof ModalFooter;
  Close: typeof ModalClose;
};

const CompoundModal = Modal as ModalCompound;
CompoundModal.Header = ModalHeader;
CompoundModal.Body = ModalBody;
CompoundModal.Footer = ModalFooter;
CompoundModal.Close = ModalClose;

export default CompoundModal;
